import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Users, UserPlus, Trash2, Loader2, Copy, ShieldCheck, Landmark } from "lucide-react";

const ROLES = ["campaign_admin", "campaign_manager", "treasurer", "compliance_officer", "media_buyer",
  "fundraising_manager", "communications_director", "digital_director", "staff", "consultant", "read_only"];

export default function TeamDirectory() {
  const navigate = useNavigate();
  const [cc, setCc] = useState(null);
  const [team, setTeam] = useState(null);
  const [invite, setInvite] = useState({ email: "", role: "campaign_manager" });
  const [sending, setSending] = useState(false);

  const load = async () => {
    const { data } = await api.get("/political-os/command-center");
    setCc(data);
    if (data.entity) {
      const t = await api.get(`/political-os/entities/${data.entity.id}/team`);
      setTeam(t.data);
    }
  };
  useEffect(() => { load().catch(() => {}); }, []); // eslint-disable-line

  const sendInvite = async () => {
    if (!invite.email.includes("@")) return toast.error("Enter a valid email");
    setSending(true);
    try {
      const { data } = await api.post(`/political-os/entities/${cc.entity.id}/invitations`, {
        email: invite.email, roles: [invite.role],
      });
      toast.success(`Invitation sent to ${invite.email}`);
      navigator.clipboard?.writeText(data.accept_url).catch(() => {});
      setInvite({ email: "", role: "campaign_manager" });
      load();
    } catch (e) { toast.error(errMsg(e, "Could not send invitation")); }
    finally { setSending(false); }
  };

  const revoke = async (iid) => {
    try { await api.delete(`/political-os/entities/${cc.entity.id}/invitations/${iid}`); toast.success("Invitation revoked"); load(); }
    catch (e) { toast.error(errMsg(e, "Could not revoke")); }
  };

  const removeMember = async (mid) => {
    try { await api.delete(`/political-os/entities/${cc.entity.id}/members/${mid}`); toast.success("Member removed"); load(); }
    catch (e) { toast.error(errMsg(e, "Could not remove member")); }
  };

  if (!cc) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;
  if (!cc.entity) {
    return (
      <div className="p-6 lg:p-8">
        <EmptyState icon={Landmark} title="No organization selected"
          description="Set up or select your political entity to manage its team."
          action={<Btn onClick={() => navigate("/onboarding/political")}>Set up political entity</Btn>} />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="team-directory-page">
      <PageHeader eyebrow="Organization" title={`${cc.entity.name} — Team`}
        subtitle="Invite a treasurer, manager or staff by email — every member gets scoped roles inside this organization only." />

      <Card data-testid="invite-card">
        <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3 flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5" /> Invite team member</div>
        <div className="flex flex-wrap gap-3 items-end">
          <Input label="Email" type="email" value={invite.email} onChange={e => setInvite(i => ({ ...i, email: e.target.value }))}
            placeholder="treasurer@campaign.com" className="flex-1 min-w-[220px]" data-testid="invite-email-input" />
          <Select label="Role" value={invite.role} onChange={e => setInvite(i => ({ ...i, role: e.target.value }))} data-testid="invite-role-select">
            {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </Select>
          <Btn onClick={sendInvite} disabled={sending} data-testid="send-invite-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Send invite
          </Btn>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">The accept link is also copied to your clipboard after sending.</p>
      </Card>

      {team && (
        <>
          <Card padded={false} data-testid="team-members-card">
            <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
              <Users className="w-4 h-4 text-cyan-300" />
              <h3 className="font-display text-lg font-bold">Members <span className="text-slate-500 text-sm font-normal">({team.members.length})</span></h3>
            </div>
            <div className="divide-y divide-white/5">
              {team.members.map(m => (
                <div key={m.id} className="px-5 py-3.5 flex items-center gap-4" data-testid={`team-member-${m.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white">{m.name || m.email}</div>
                    <div className="text-[11px] text-slate-500">{m.email}</div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {(m.roles || []).map(r => (
                      <span key={r} className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${["candidate", "campaign_admin"].includes(r) ? "text-cyan-300 border-cyan-400/30" : "text-slate-400 border-white/15"}`}>
                        {r.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                  <button onClick={() => removeMember(m.id)} data-testid={`remove-member-${m.id}`}
                    className="text-slate-600 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {team.members.length === 0 && <div className="px-5 py-4 text-sm text-slate-500">No members yet — send the first invitation above.</div>}
            </div>
          </Card>

          {team.invitations.length > 0 && (
            <Card padded={false} data-testid="pending-invites-card">
              <div className="px-5 py-3 border-b border-white/10 text-xs uppercase tracking-wider text-slate-400 font-medium">Pending invitations ({team.invitations.length})</div>
              <div className="divide-y divide-white/5">
                {team.invitations.map(i => (
                  <div key={i.id} className="px-5 py-3 flex items-center gap-4" data-testid={`pending-invite-${i.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white">{i.email}</div>
                      <div className="text-[11px] text-slate-500">{(i.roles || []).join(", ").replace(/_/g, " ")} · invited by {i.invited_by} · expires {i.expires_at?.slice(0, 10)}</div>
                    </div>
                    <button onClick={() => revoke(i.id)} data-testid={`revoke-invite-${i.id}`}
                      className="text-xs text-slate-500 hover:text-red-400 transition-colors">Revoke</button>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
